// Single source of truth for every color in Hoopoe (PLAN §12.1–12.2).
// Feeds Tailwind (via the generated apps/web/src/styles/tokens.css) and the visualization libraries.
// The interface is neutral grey; hue is reserved for entities and for red/amber/green states.

/** Neutral grey scale (no hue). */
export const gray = {
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#e5e5e5',
  300: '#d4d4d4',
  400: '#a3a3a3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  800: '#262626',
  900: '#171717',
  950: '#0a0a0a',
} as const;

type Mode = { light: string; dark: string };

/** Semantic UI roles. Everything except danger/warning/success is grey. */
export const semantic = {
  background: { light: gray[50], dark: gray[950] },
  foreground: { light: gray[950], dark: gray[50] },
  surface: { light: '#ffffff', dark: gray[900] },
  'surface-raised': { light: '#ffffff', dark: gray[800] },
  muted: { light: gray[100], dark: gray[800] },
  'muted-foreground': { light: gray[600], dark: gray[400] },
  border: { light: gray[200], dark: gray[800] },
  /** Input borders identify the control, so they meet 3:1 non-text contrast (WCAG 1.4.11) on every surface. */
  input: { light: gray[500], dark: gray[500] },
  ring: { light: gray[500], dark: gray[400] },
  primary: { light: gray[900], dark: gray[50] },
  'primary-foreground': { light: gray[50], dark: gray[900] },
  secondary: { light: gray[100], dark: gray[800] },
  'secondary-foreground': { light: gray[900], dark: gray[50] },
  /** States: the mark color, plus a text-safe (≥4.5:1) foreground on the surface. Always paired with an icon + label. */
  danger: { light: '#d03b3b', dark: '#d03b3b' },
  'danger-foreground': { light: '#b42323', dark: '#f08c8c' },
  warning: { light: '#fab219', dark: '#fab219' },
  'warning-foreground': { light: '#8a5a00', dark: '#fbc75a' },
  success: { light: '#0ca30c', dark: '#0ca30c' },
  'success-foreground': { light: '#006300', dark: '#5fcf5f' },
} as const satisfies Record<string, Mode>;

/**
 * 12-slot categorical entity palette, in fixed order (never cycled). Validated with the dataviz
 * six-checks validator on the adjacent pairlist: light vs #ffffff, dark vs gray-900. Worst adjacent
 * CVD ΔE 9.1 light / 8.4 dark; normal-vision ΔE ≥ 19.3. Light slots 3, 4, 5 are < 3:1 on white, so
 * entity marks always carry a text label and a thin outline ring (relief rule). Beyond 12 → grey.
 */
export const entityPalette: readonly Mode[] = [
  { light: '#2a78d6', dark: '#3987e5' }, // blue
  { light: '#eb6834', dark: '#d95926' }, // orange
  { light: '#1baf7a', dark: '#199e70' }, // aqua
  { light: '#eda100', dark: '#c98500' }, // yellow
  { light: '#e87ba4', dark: '#d55181' }, // magenta
  { light: '#008300', dark: '#008300' }, // green
  { light: '#4a3aa7', dark: '#9085e9' }, // violet
  { light: '#e34948', dark: '#e66767' }, // red
  { light: '#1a9bc4', dark: '#1e93bd' }, // cyan
  { light: '#a85a14', dark: '#b87a3a' }, // brown
  { light: '#8f3a8f', dark: '#b460b4' }, // plum
  { light: '#7d8c1f', dark: '#83921c' }, // olive
];

/** Entities without a color (unknown type, cluster beyond slot 12). */
export const entityFallback: Mode = { light: gray[400], dark: gray[500] };

export const ENTITY_SLOTS = entityPalette.length;

/**
 * CSS variable for an entity color slot (1-based); out-of-range → fallback grey. Uses the `--hp-*` runtime
 * variables (always emitted, switch with the theme) rather than Tailwind's `--color-*`, which `@theme inline`
 * only inlines into utility classes. Use this for SVG/canvas/visualization libraries.
 */
export const entityColorVar = (slot: number | null | undefined): string =>
  slot && slot >= 1 && slot <= ENTITY_SLOTS ? `var(--hp-entity-${slot})` : 'var(--hp-entity-none)';

/** Runtime CSS variable for a semantic role, for SVG/canvas use. */
export const semanticVar = (role: keyof typeof semantic): string => `var(--hp-${role})`;

export const fonts = {
  sans: "'Inter Variable', 'Noto Sans Arabic Variable', ui-sans-serif, system-ui, sans-serif",
  mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
} as const;

/** Renders the Tailwind v4 theme + light/dark variables. Output is committed as tokens.css and checked by a test. */
export function renderTokensCss(): string {
  const vars = (mode: 'light' | 'dark') =>
    [
      ...Object.entries(semantic).map(([k, v]) => `  --hp-${k}: ${v[mode]};`),
      ...entityPalette.map((c, i) => `  --hp-entity-${i + 1}: ${c[mode]};`),
      `  --hp-entity-none: ${entityFallback[mode]};`,
    ].join('\n');
  const theme = [
    '  --color-*: initial;',
    '  --color-white: #ffffff;',
    '  --color-black: #000000;',
    ...Object.entries(gray).map(([k, v]) => `  --color-gray-${k}: ${v};`),
    ...Object.keys(semantic).map(k => `  --color-${k}: var(--hp-${k});`),
    ...entityPalette.map((_, i) => `  --color-entity-${i + 1}: var(--hp-entity-${i + 1});`),
    '  --color-entity-none: var(--hp-entity-none);',
    `  --font-sans: ${fonts.sans};`,
    `  --font-mono: ${fonts.mono};`,
  ].join('\n');
  return `/* GENERATED from packages/shared/src/design/tokens.ts by \`pnpm tokens\`. Do not edit. */
/* The default Tailwind palette is removed: only these colors exist in the UI. */
@theme inline {
${theme}
}

:root {
  color-scheme: light;
${vars('light')}
}

@media (prefers-color-scheme: dark) {
  :root:where(:not([data-theme='light'])) {
    color-scheme: dark;
${vars('dark').replace(/^ {2}/gm, '    ')}
  }
}

:root[data-theme='dark'] {
  color-scheme: dark;
${vars('dark')}
}
`;
}
